import { Box, Button, DialogActions, Stack, Typography } from '@mui/material';
import colors from 'common/constants/colors';
import PropTypes from 'prop-types';

import { commitBeforePrintText } from '../utils/bulkLotNdcHelperText';

/**
 * POM-6534 — the leave-out choice sits in the same block as the count it changes, in the amber the
 * separate panel used to carry, so the number and the decision behind it are read together.
 */
const LEAVE_OUT_BLOCK_SX = {
  px: 1.5,
  py: 1,
  borderRadius: 1,
  bgcolor: colors.warningAmberLight,
  border: `1px solid ${colors.warningAmberBorder}`,
  borderLeft: `4px solid ${colors.warningAmber}`,
};

/**
 * POM-6010 — the review dialog's bottom bar.
 *
 * States what the batch will actually do, then offers the controls to do it. The count line is the
 * honest summary the user prints against: "24 of 25 labels will print · 1 excluded" rather than a
 * bare Print button that quietly drops one.
 *
 * The print controls themselves are supplied by the caller (`renderPrintActions`), because their
 * enablement has two owners: whether the batch has settled — which this dialog knows — and whether
 * the PDF exists and the user may mark printed, which only PrintLabelButton knows. `locked` is this
 * half of that rule, and `lockedHint` says why it is locked while an edit waits on a commit.
 *
 * `busy` is the weaker half of the same signal, for controls that do not need a finished batch:
 * Regenerate rebuilds the labels rather than consuming them, so it stays available while excluded
 * rows sit unacknowledged and only goes dead while something is actually in flight.
 */
const BulkLotNdcPrintBar = ({
  counts,
  excludedCount,
  excludedPanel,
  unreviewedCount,
  helperText,
  printLocked,
  actionsBusy,
  committing,
  renderPrintNotice,
  renderPrintActions,
  onClose,
}) => {
  const lockedHint =
    !committing && counts.changed > 0 ? commitBeforePrintText(counts.changed, counts.claimed) : '';

  return (
    <DialogActions
      sx={{
        px: 3,
        py: 2,
        borderTop: '1px solid',
        borderColor: 'divider',
        justifyContent: 'space-between',
        gap: 2,
        // Seven controls plus the count block: on a narrow viewport this has to fold rather than
        // overflow.
        flexWrap: 'wrap',
        rowGap: 1.5,
      }}
    >
      {/* minWidth: 0 so the count block yields first when space runs short — it is the one thing
        here that reflows instead of being clipped. */}
      <Box sx={{ minWidth: 0, flex: '1 1 420px' }}>
        <Box sx={excludedPanel ? LEAVE_OUT_BLOCK_SX : undefined}>
          <Typography variant='body2' sx={{ fontWeight: 600 }}>
            {`${counts.total - excludedCount} of ${counts.total} labels will print`}
            {/*
            Two different things to say, and saying the wrong one is how this line stops being
            trusted. Rows dropped by the user are "excluded"; rows printing with a lot that may be
            wrong are still printing, so calling them excluded would be a lie about the output.
          */}
            {excludedCount > 0 && (
              <Box component='span' sx={{ color: colors.warningAmberText, fontWeight: 600 }}>
                {` · ${excludedCount} excluded`}
              </Box>
            )}
            {excludedCount === 0 && counts.blocked > 0 && (
              <Box component='span' sx={{ color: colors.warningAmberText, fontWeight: 600 }}>
                {` · ${counts.blocked} need${counts.blocked > 1 ? '' : 's'} attention`}
              </Box>
            )}
          </Typography>
          {/* Said out loud rather than absorbed into the total. These items print exactly as they
            did before this screen existed, but nothing here has checked their lot. */}
          {unreviewedCount > 0 && (
            <Typography variant='caption' color='text.secondary' component='div'>
              {`${unreviewedCount} could not be reviewed (no longer in the queue, or outside your facilities) and print unchanged.`}
            </Typography>
          )}
          {excludedPanel}
        </Box>
        {helperText && (
          <Typography variant='caption' color='text.secondary' component='div' sx={{ mt: 0.5 }}>
            {helperText}
          </Typography>
        )}
      </Box>
      <Stack direction='row' spacing={1} alignItems='center' sx={{ flexWrap: 'wrap', rowGap: 1 }}>
        {renderPrintNotice({ busy: actionsBusy, lockedHint })}
        {renderPrintActions({ locked: printLocked, busy: actionsBusy, lockedHint })}
        {/* Close routes back through the dialog's guard, which prompts if edits are uncommitted. */}
        <Button onClick={onClose} variant='outlined' color='secondary' disabled={committing}>
          Close
        </Button>
      </Stack>
    </DialogActions>
  );
};

BulkLotNdcPrintBar.propTypes = {
  counts: PropTypes.shape({
    total: PropTypes.number,
    blocked: PropTypes.number,
    changed: PropTypes.number,
    /** POM-6536 — how many of `changed` are claims. */
    claimed: PropTypes.number,
  }).isRequired,
  /** How many rows the user has opted to drop — 0 when the whole batch is printing. */
  excludedCount: PropTypes.number,
  /** The leave-out checkbox and its caption, shown inside the count block when rows are held back. */
  excludedPanel: PropTypes.node,
  /** Selected items the review could not return. 0 until the review has loaded. */
  unreviewedCount: PropTypes.number,
  helperText: PropTypes.string.isRequired,
  printLocked: PropTypes.bool.isRequired,
  actionsBusy: PropTypes.bool.isRequired,
  committing: PropTypes.bool.isRequired,
  renderPrintNotice: PropTypes.func,
  renderPrintActions: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

BulkLotNdcPrintBar.defaultProps = {
  excludedCount: 0,
  excludedPanel: null,
  unreviewedCount: 0,
  renderPrintNotice: () => null,
};

export default BulkLotNdcPrintBar;
